import { useState, useEffect } from "react";
import { FaTrash, FaEye, FaEdit, FaDownload, FaSearch, FaUser, FaPhone, FaEnvelope, FaMoneyBill, FaUsers, FaCalendar, FaCopy, FaCheck, FaPlus, FaHistory, FaExchangeAlt, FaCoins } from "react-icons/fa";
import { utils, writeFile } from "xlsx";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [isCreditDebitModalOpen, setIsCreditDebitModalOpen] = useState(false);
  const [selectedUserForTransaction, setSelectedUserForTransaction] = useState(null);
  const [transactionType, setTransactionType] = useState("credit");
  const [transactionAmount, setTransactionAmount] = useState("");
  const [transactionReason, setTransactionReason] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [isEditTransactionModalOpen, setIsEditTransactionModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [editTransactionAmount, setEditTransactionAmount] = useState("");
  const [editTransactionReason, setEditTransactionReason] = useState("");
  const [editTransactionAdminNote, setEditTransactionAdminNote] = useState("");
  const [editTransactionStatus, setEditTransactionStatus] = useState("completed");
  const [editTransactionLoading, setEditTransactionLoading] = useState(false);
  const usersPerPage = 8;

  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  // Get all users API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get("https://api.dreamtoday.in/api/admin/getallusers");

      if (response.data && response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      alert("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  // Delete user API
  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const response = await axios.delete(`https://api.dreamtoday.in/api/admin/deleteuser/${userId}`);

        if (response.data && response.data.success) {
          alert("User deleted successfully");
          setUsers(users.filter((user) => user._id !== userId));
        } else {
          alert(response.data.message || "Failed to delete user");
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        alert(error.response?.data?.message || "Failed to delete user");
      }
    }
  };

  // Phone number validation function
  const validatePhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, '');

    if (cleaned.length > 10) {
      setPhoneError("Phone number cannot exceed 10 digits");
      return false;
    } else if (cleaned.length > 0 && cleaned.length < 10) {
      setPhoneError("Phone number must be exactly 10 digits");
      return false;
    } else {
      setPhoneError("");
      return true;
    }
  };

  // Handle phone input change with validation
  const handlePhoneChange = (e) => {
    const input = e.target.value;
    const cleaned = input.replace(/\D/g, '').slice(0, 10);

    setEditingUser({ ...editingUser, phone: cleaned });
    validatePhoneNumber(cleaned);
  };

  // Edit user API
  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!validatePhoneNumber(editingUser.phone)) {
      alert("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      const response = await axios.put(
        `https://api.dreamtoday.in/api/admin/edituser/${editingUser._id}`,
        {
          name: editingUser.name,
          email: editingUser.email,
          phone: editingUser.phone
        }
      );

      if (response.data && response.data.success) {
        alert("User updated successfully");
        setUsers(users.map(user =>
          user._id === editingUser._id ? response.data.user : user
        ));
        setIsEditModalOpen(false);
        setEditingUser(null);
        setPhoneError("");
      } else {
        alert(response.data.message || "Failed to update user");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      alert(error.response?.data?.message || "Failed to update user");
    }
  };

  // Credit/Debit transaction handler
  const handleCreditDebit = async (e) => {
    e.preventDefault();

    const amount = parseFloat(transactionAmount);
    if (isNaN(amount) || amount <= 0) {
      alert("Please enter a valid amount greater than 0");
      return;
    }

    if (!transactionReason.trim()) {
      alert("Please provide a reason for this transaction");
      return;
    }

    setTransactionLoading(true);

    try {
      const endpoint = transactionType === "credit"
        ? `https://api.dreamtoday.in/api/admin/credit/${selectedUserForTransaction._id}`
        : `https://api.dreamtoday.in/api/admin/debit/${selectedUserForTransaction._id}`;

      const payload = {
        amount: amount,
        reason: transactionReason,
        adminNote: adminNote || "No additional notes"
      };

      const response = await axios.post(endpoint, payload);

      if (response.data && response.data.success) {
        alert(`${transactionType === "credit" ? "Credited" : "Debited"} amount successfully!`);
        await fetchUsers();
        setIsCreditDebitModalOpen(false);
        resetTransactionForm();
      } else {
        alert(response.data.message || `Failed to ${transactionType} amount`);
      }
    } catch (error) {
      console.error(`Error ${transactionType}ing user:`, error);
      alert(error.response?.data?.message || `Failed to ${transactionType} amount`);
    } finally {
      setTransactionLoading(false);
    }
  };

  // Reset transaction form
  const resetTransactionForm = () => {
    setTransactionAmount("");
    setTransactionReason("");
    setAdminNote("");
    setTransactionType("credit");
    setSelectedUserForTransaction(null);
  };

  // Open credit/debit modal
  const openCreditDebitModal = (user) => {
    setSelectedUserForTransaction(user);
    setIsCreditDebitModalOpen(true);
  };

  // Open edit transaction modal
  const openEditTransactionModal = (transaction, userId, transactionId) => {
    setSelectedTransaction({
      ...transaction,
      userId: userId,
      transactionId: transactionId
    });
    setEditTransactionAmount(transaction.coinsAdded || transaction.coinsDeducted || 0);
    setEditTransactionReason(transaction.reason || "");
    setEditTransactionAdminNote(transaction.adminNote || "");
    setEditTransactionStatus(transaction.status || "completed");
    setIsEditTransactionModalOpen(true);
  };

  // Edit transaction handler (PUT request)
  const handleEditTransaction = async (e) => {
    e.preventDefault();

    const amount = parseFloat(editTransactionAmount);
    if (isNaN(amount) || amount <= 0) {
      alert("Please enter a valid amount greater than 0");
      return;
    }

    if (!editTransactionReason.trim()) {
      alert("Please provide a reason for this transaction");
      return;
    }

    setEditTransactionLoading(true);

    try {
      const response = await axios.put(
        `https://api.dreamtoday.in/api/admin/transaction/${selectedTransaction.transactionId}`,
        {
          amount: amount,
          reason: editTransactionReason,
          adminNote: editTransactionAdminNote || "No additional notes",
          status: editTransactionStatus
        }
      );

      if (response.data && response.data.success) {
        alert("Transaction updated successfully!");
        await fetchUsers();
        setIsEditTransactionModalOpen(false);
        resetEditTransactionForm();
      } else {
        alert(response.data.message || "Failed to update transaction");
      }
    } catch (error) {
      console.error("Error updating transaction:", error);
      alert(error.response?.data?.message || "Failed to update transaction");
    } finally {
      setEditTransactionLoading(false);
    }
  };

  // Reset edit transaction form
  const resetEditTransactionForm = () => {
    setSelectedTransaction(null);
    setEditTransactionAmount("");
    setEditTransactionReason("");
    setEditTransactionAdminNote("");
    setEditTransactionStatus("completed");
  };

  const exportData = (type) => {
    const exportUsers = filteredUsers.map((user) => ({
      ID: user._id,
      Name: user.name || "N/A",
      Email: user.email || "N/A",
      Phone: user.phone || "N/A",
      Balance: user.wallet?.balance || 0,
      TotalTransactions: user.wallet?.history?.length || 0,
      TotalPlans: user.myPlans?.length || 0,
      Joined: new Date(user.createdAt).toLocaleDateString(),
    }));

    const ws = utils.json_to_sheet(exportUsers);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Users");
    writeFile(wb, `users_${new Date().toISOString().split('T')[0]}.${type}`);
  };

  const openUserDetails = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser({
      _id: user._id,
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || ''
    });
    setPhoneError("");
    setIsEditModalOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredUsers = users.filter((user) =>
    (user.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (user.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (user.phone || "").includes(search)
  );

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const getStatusColor = (balance) => {
    if (balance > 1000) return "text-green-600 bg-green-50 border border-green-200";
    if (balance > 0) return "text-blue-600 bg-blue-50 border border-blue-200";
    return "text-gray-600 bg-gray-50 border border-gray-200";
  };

  const calculateStats = () => {
    const totalBalance = users.reduce((sum, user) => sum + (user.wallet?.balance || 0), 0);
    const totalTransactions = users.reduce((sum, user) => sum + (user.wallet?.history?.length || 0), 0);
    const totalPlans = users.reduce((sum, user) => sum + (user.myPlans?.length || 0), 0);

    return { totalBalance, totalTransactions, totalPlans };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className=" bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
        <p className="text-gray-600">Manage all registered users in the system</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg mr-4">
              <FaUsers className="text-blue-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg mr-4">
              <FaMoneyBill className="text-green-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Balance</p>
              <p className="text-2xl font-bold text-gray-900">₹{stats.totalBalance}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg mr-4">
              <FaUsers className="text-purple-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Plans</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalPlans}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-amber-100 rounded-lg mr-4">
              <FaCalendar className="text-amber-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Today's New</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(u => {
                  const today = new Date().toDateString();
                  const userDate = new Date(u.createdAt).toDateString();
                  return today === userDate;
                }).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Export */}
      <div className="bg-white rounded-xl shadow border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by name, email or phone..."
              className="w-full px-4 py-3 pl-12 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => exportData("csv")}
              className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2 font-medium"
            >
              <FaDownload />
              CSV
            </button>
            <button
              onClick={() => exportData("xlsx")}
              className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2 font-medium"
            >
              <FaDownload />
              Excel
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  User Details
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Wallet Balance
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Account Date
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentUsers.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-6">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                        <FaUser className="text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name || "No Name"}
                        </div>
                        <div className="text-xs text-gray-500 font-mono flex items-center">
                          ID: {user._id?.substring(0, 10)}...
                          <button
                            onClick={() => copyToClipboard(user._id)}
                            className="ml-2 text-gray-400 hover:text-blue-600 transition"
                            title="Copy ID"
                          >
                            {copiedId === user._id ? <FaCheck className="text-green-500" /> : <FaCopy />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="py-4 px-6">
                    <div className="space-y-1">
                      <div className="flex items-center text-sm">
                        <FaEnvelope className="text-gray-400 mr-2" />
                        <span className="text-gray-900">{user.email}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <FaPhone className="text-gray-400 mr-2" />
                        <span className="text-gray-600">{user.phone}</span>
                      </div>
                    </div>
                  </td>

                  <td className="py-4 px-6">
                    <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${getStatusColor(user.wallet?.balance || 0)}`}>
                      <FaMoneyBill className="mr-1.5" />
                      ₹{user.wallet?.balance || 0}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {user.wallet?.history?.length || 0} transactions
                    </div>
                  </td>

                  <td className="py-4 px-6">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-gray-500">
                        {new Date(user.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </td>

                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openUserDetails(user)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition border border-blue-200 hover:border-blue-300"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => navigate('/users/' + user._id + '/transactions')}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition border border-blue-200 hover:border-blue-300"
                        title="View Transactions"
                      >
                        <FaCoins />
                      </button>
                      <button
                        onClick={() => openEditModal(user)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition border border-green-200 hover:border-green-300"
                        title="Edit User"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => openCreditDebitModal(user)}
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition border border-purple-200 hover:border-purple-300"
                        title="Add Credit/Debit"
                      >
                        <FaExchangeAlt />
                      </button>
                      <button
                        onClick={() => handleDelete(user._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition border border-red-200 hover:border-red-300"
                        title="Delete User"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {currentUsers.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <FaUsers className="h-16 w-16 mx-auto" />
            </div>
            <p className="text-gray-500 text-lg font-medium">No users found</p>
            <p className="text-gray-400 mt-2">Try adjusting your search criteria</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredUsers.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold">{indexOfFirstUser + 1}</span> to{" "}
            <span className="font-semibold">{Math.min(indexOfLastUser, filteredUsers.length)}</span> of{" "}
            <span className="font-semibold">{filteredUsers.length}</span> users
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              Previous
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`w-10 h-10 rounded-lg transition ${currentPage === pageNumber
                      ? "bg-blue-600 text-white"
                      : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {isModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-6 pb-4 border-b">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">User Details</h2>
                  <p className="text-gray-600">Complete information about the user</p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Personal Info Section */}
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <FaUser className="mr-2" /> Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500">Full Name</p>
                      <p className="text-lg font-semibold text-gray-900">{selectedUser.name}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500">Email Address</p>
                      <p className="text-lg font-semibold text-gray-900">{selectedUser.email}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500">Phone Number</p>
                      <p className="text-lg font-semibold text-gray-900">{selectedUser.phone}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500">User ID</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-mono text-gray-900 break-all">{selectedUser._id}</p>
                        <button
                          onClick={() => copyToClipboard(selectedUser._id)}
                          className="text-gray-400 hover:text-blue-600 transition"
                        >
                          {copiedId === selectedUser._id ? <FaCheck className="text-green-500" /> : <FaCopy />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Wallet Section */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <FaMoneyBill className="mr-2" /> Wallet Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500">Current Balance</p>
                      <p className="text-3xl font-bold text-green-600">
                        ₹{selectedUser.wallet?.balance || 0}
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500">Total Transactions</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedUser.wallet?.history?.length || 0}
                      </p>
                    </div>
                  </div>

                  {/* Transaction History */}
                  {selectedUser.wallet?.history && selectedUser.wallet.history.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-semibold text-gray-700 mb-3 flex items-center justify-between">
                        <span>Transaction History</span>
                        <span className="text-xs text-gray-500">Click edit icon to modify transaction</span>
                      </h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {selectedUser.wallet.history.map((transaction, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition group">
                            <div className="flex-1">
                              <p className="font-medium">
                                {transaction.coinsAdded > 0 ? "Credit" : "Debit"} Transaction
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(transaction.createdAt).toLocaleString()}
                              </p>
                              {transaction.reason && (
                                <p className="text-xs text-gray-600 mt-1">
                                  Reason: {transaction.reason}
                                </p>
                              )}
                              {transaction.adminNote && (
                                <p className="text-xs text-gray-500">
                                  Note: {transaction.adminNote}
                                </p>
                              )}
                              {transaction.status && (
                                <p className={`text-xs mt-1 font-semibold ${transaction.status === 'completed' ? 'text-green-600' : 'text-yellow-600'
                                  }`}>
                                  Status: {transaction.status}
                                </p>
                              )}
                            </div>
                            <div className="text-right flex items-center gap-2">
                              <div>
                                {transaction.coinsAdded > 0 && (
                                  <p className="text-lg font-bold text-green-600">
                                    +₹{transaction.coinsAdded}
                                  </p>
                                )}
                                {transaction.coinsDeducted > 0 && (
                                  <p className="text-lg font-bold text-red-600">
                                    -₹{transaction.coinsDeducted}
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={() => openEditTransactionModal(
                                  transaction,
                                  selectedUser._id,
                                  transaction._id || `trans_${index}`
                                )}
                                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition opacity-0 group-hover:opacity-100"
                                title="Edit Transaction"
                              >
                                <FaEdit />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Plans Section */}
                {selectedUser.myPlans && selectedUser.myPlans.length > 0 && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <FaUsers className="mr-2" /> Purchased Plans
                    </h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {selectedUser.myPlans.map((plan, index) => (
                        <div key={plan._id} className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-xs text-gray-500">Plan ID</p>
                              <p className="text-sm font-mono">{plan.planId.substring(0, 10)}...</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Coins</p>
                              <p className="text-sm font-bold text-blue-600">{plan.coins}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Bonus</p>
                              <p className="text-sm font-bold text-green-600">{plan.bonusCoins}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Status</p>
                              <p className={`text-xs font-semibold px-2 py-1 rounded-full inline-block ${plan.isPurchased ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                {plan.isPurchased ? 'Active' : 'Inactive'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Purchased</p>
                              <p className="text-xs">{new Date(plan.purchasedAt).toLocaleDateString()}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Expires</p>
                              <p className="text-xs">{new Date(plan.expiredAt).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Account Timeline */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <FaCalendar className="mr-2" /> Account Timeline
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500">Account Created</p>
                      <p className="font-medium text-gray-900">
                        {new Date(selectedUser.createdAt).toLocaleString()}
                      </p>
                    </div>
                    
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t flex gap-3">
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    openEditModal(selectedUser);
                  }}
                  className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                >
                  Edit User
                </button>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    openCreditDebitModal(selectedUser);
                  }}
                  className="flex-1 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
                >
                  Credit/Debit
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  Close Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal with Phone Validation */}
      {isEditModalOpen && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit User Details</h2>
                <button
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setPhoneError("");
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={editingUser.name || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={editingUser.email || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-xs text-gray-500">(10 digits)</span>
                  </label>
                  <input
                    type="tel"
                    value={editingUser.phone || ''}
                    onChange={handlePhoneChange}
                    placeholder="Enter 10-digit mobile number"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${phoneError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    required
                  />
                  {phoneError && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <span className="mr-1">⚠️</span>
                      {phoneError}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {editingUser.phone?.length || 0}/10 digits
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={!!phoneError}
                    className={`flex-1 py-3 rounded-lg transition font-medium ${phoneError
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                  >
                    Update User
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setPhoneError("");
                    }}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Credit/Debit Modal with Dropdown */}
      {isCreditDebitModalOpen && selectedUserForTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm">

          {/* Modal Container */}
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg 
      max-h-[95vh] flex flex-col overflow-hidden">

            {/* 🔥 HEADER (Sticky) */}
            <div className="p-4 sm:p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex justify-between items-start gap-3">
                <div>
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
                    Credit/Debit Amount
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    User:{" "}
                    <span className="font-semibold">
                      {selectedUserForTransaction.name || "No Name"}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500">
                    Balance: ₹{selectedUserForTransaction.wallet?.balance || 0}
                  </p>
                </div>

                <button
                  onClick={() => {
                    setIsCreditDebitModalOpen(false);
                    resetTransactionForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* 🔥 BODY (Scrollable) */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <form onSubmit={handleCreditDebit} className="space-y-4">

                {/* Type */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Transaction Type
                  </label>
                  <select
                    value={transactionType}
                    onChange={(e) => setTransactionType(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="credit">Credit</option>
                    <option value="debit">Debit</option>
                  </select>
                </div>

                {/* Amount */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={transactionAmount}
                    onChange={(e) => setTransactionAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full px-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Reason */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Reason *
                  </label>
                  <input
                    type="text"
                    value={transactionReason}
                    onChange={(e) => setTransactionReason(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border rounded-lg"
                    required
                  />
                </div>

                {/* Note */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Admin Note
                  </label>
                  <textarea
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2.5 text-sm border rounded-lg resize-none"
                  />
                </div>

                {/* Info */}
                <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600">
                  This will{" "}
                  <span className="font-semibold">
                    {transactionType === "credit" ? "add" : "deduct"}
                  </span>{" "}
                  ₹{transactionAmount || 0}
                </div>

              </form>
            </div>

            {/* 🔥 FOOTER (Sticky Buttons) */}
            <div className="p-4 border-t bg-white flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleCreditDebit}
                disabled={transactionLoading}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium text-white ${transactionType === "credit"
                  ? "bg-purple-600"
                  : "bg-orange-600"
                  }`}
              >
                {transactionLoading ? "Processing..." : "Submit"}
              </button>

              <button
                onClick={() => {
                  setIsCreditDebitModalOpen(false);
                  resetTransactionForm();
                }}
                className="flex-1 bg-gray-100 py-2.5 rounded-lg text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Transaction Modal (PUT Request) */}
      {isEditTransactionModalOpen && selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Edit Transaction</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Update transaction details (PUT Request)
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsEditTransactionModalOpen(false);
                    resetEditTransactionForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleEditTransaction} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={editTransactionAmount}
                    onChange={(e) => setEditTransactionAmount(e.target.value)}
                    placeholder="Enter new amount"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                    min="1"
                    step="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editTransactionReason}
                    onChange={(e) => setEditTransactionReason(e.target.value)}
                    placeholder="e.g., Updated bonus amount"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Note
                  </label>
                  <textarea
                    value={editTransactionAdminNote}
                    onChange={(e) => setEditTransactionAdminNote(e.target.value)}
                    placeholder="Add any additional notes..."
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={editTransactionStatus}
                    onChange={(e) => setEditTransactionStatus(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  <p className="text-xs text-yellow-800">
                    <span className="font-semibold">⚠️ Warning:</span> Editing this transaction will update the user's wallet balance accordingly. Please ensure the changes are accurate.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={editTransactionLoading}
                    className="flex-1 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editTransactionLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Updating...
                      </span>
                    ) : (
                      "Update Transaction (PUT)"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditTransactionModalOpen(false);
                      resetEditTransactionForm();
                    }}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition font-medium"
                    disabled={editTransactionLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;